import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useOutletContext } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faLock, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { SEOHelmet } from 'components/SEO';
import { PlayPrimaryButton } from '../../components/PlayPrimaryButton';
import { usePutDecklistMutation } from 'store/services/play/playApi';
import type { DecklistInvalidDetails } from 'types/interfaces/play';
import { decklistInvalidDetails, playErrorMessage } from 'utils/play/playErrors';
import { deckRuleSentence, decklistIssues, issueText, parseDecklistSections } from 'utils/play/parseDecklistSections';
import { playPath } from 'utils/play/inviteUrl';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import { playerTabs } from '../../layout/playTabs';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { useTournament } from '../../hooks/useTournament';
import { TournamentLoading } from '../Tournament/TournamentHomePage';
import '../Host/HostCreate.css';
import './PlayerHome.css';
import './DeckPage.css';

/**
 * Decision 18 (paste only, ruling 7), decklist submit review: a full-screen
 * step (no card, no bottom nav) with the intro line, one editable textarea,
 * and a pinned footer with the count row,
 * the gradient Submit (not tappable until the list is legal) and Skip for
 * now. The server validates and resolves names (400 DECKLIST_INVALID details
 * render under the textarea). After round 1 starts the list is locked and the
 * Deck tab shows the stored list.
 */
const DeckPage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const navigate = useNavigate();
  const { state, isLoading, notFound } = useTournament(code);
  const [putDecklist, { isLoading: saving }] = usePutDecklistMutation();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [seededFrom, setSeededFrom] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<DecklistInvalidDetails | null>(null);

  const existing = state?.myDecklist ?? null;
  // Seed the editor from the stored list once per stored version.
  useEffect(() => {
    if (!existing) return;
    const key = `${existing.updatedAt ?? ''}|${existing.rawText.length}`;
    if (seededFrom === key) return;
    setSeededFrom(key);
    setText(existing.rawText);
  }, [existing, seededFrom]);

  const sections = useMemo(() => parseDecklistSections(text), [text]);
  const formatLabel = state?.tournament.format ?? state?.tournament.formatLabel ?? '';
  const issues = useMemo(() => decklistIssues(sections, formatLabel), [sections, formatLabel]);

  if (notFound) return <PlayNotFound reason="missing" />;
  if (isLoading || !state) return <TournamentLoading />;
  if (state.viewer.role !== 'PLAYER') return <Navigate replace to={playPath(code)} />;
  if (!state.tournament.decklistRequired) return <Navigate replace to={playPath(code)} />;

  const roundOneStarted = state.rounds.some((r) => r.number === 1 && r.startedAt);
  const locked = !!existing?.lockedAt || roundOneStarted;
  const showEditor = !existing || (editing && !locked);
  const hasText = text.trim().length > 0;
  const legal = hasText && issues.length === 0;
  const showSkip = !existing && !(state.tournament.decklistRequired && roundOneStarted);

  const submit = async () => {
    if (!legal || saving) return;
    setError(null);
    setDetails(null);
    try {
      await putDecklist({ id: state.tournament.id, code, rawText: text }).unwrap();
      setEditing(false);
      if (!existing) navigate(playPath(code), { replace: true });
    } catch (err) {
      const d = decklistInvalidDetails(err);
      if (d) {
        setDetails(d);
        setError(null);
      } else {
        setError(playErrorMessage(err, "Couldn't save the decklist. Try again."));
      }
    }
  };

  if (showEditor) {
    const firstIssue = issues[0];
    return (
      <PlayLayout
        variant="step"
        title={existing ? 'Edit decklist' : 'Submit your decklist'}
        backTo={existing ? () => setEditing(false) : undefined}
        className="play-layout--deck"
      >
        <SEOHelmet title="Decklist - TradeMagic Play" description="Submit your decklist." noindex />
        <div className="play-deck-step" data-testid="deck-editor">
          <p className="play-deck-step__intro" data-testid="deck-intro">
            {state.tournament.formatLabel} · {deckRuleSentence(formatLabel)} You can edit until round 1 starts.
          </p>

          <textarea
            id="play-deck-text"
            className="play-deck-step__textarea"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDetails(null);
            }}
            placeholder="4 Lightning Bolt"
            aria-label="Decklist"
            spellCheck={false}
            data-testid="deck-textarea"
          />

          {details && (
            <div className="play-deck__invalid" role="alert" data-testid="deck-invalid">
              {details.issues.length > 0 && <p className="play-deck__invalid-title">{details.issues.map((i) => issueText(i, details)).join(' · ')}</p>}
              {details.unresolved.length > 0 && (
                <>
                  <p className="play-deck__invalid-title">Couldn't find these cards</p>
                  <ul>
                    {details.unresolved.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          {error && (
            <p className="play-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="play-step-footer" data-testid="deck-footer">
          <div className="play-deck-step__counts">
            <span className="play-deck-step__count" data-testid="deck-counts">
              {sections.mainCount} main · {sections.sideCount} sideboard
            </span>
            {hasText && legal && (
              <span className="play-deck-step__status play-deck-step__status--ok" data-testid="deck-legal">
                <FontAwesomeIcon icon={faCircleCheck} />
                Legal size
              </span>
            )}
            {hasText && firstIssue && (
              <span className="play-deck-step__status play-deck-step__status--warn" data-testid="deck-issue">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {issueText(firstIssue, sections)}
              </span>
            )}
          </div>
          <PlayPrimaryButton label={existing ? 'Save changes' : 'Submit decklist'} disabled={!legal || saving} loading={saving} onClick={submit} testId="deck-submit" />
          {showSkip && (
            <button type="button" className="play-deck-step__skip" onClick={() => navigate(playPath(code))} data-testid="deck-skip">
              Skip for now
            </button>
          )}
        </div>
      </PlayLayout>
    );
  }

  return (
    <PlayLayout title={state.tournament.name} subtitle="Decklist" tabs={playerTabs(code, 'deck', true)}>
      <SEOHelmet title="Decklist - TradeMagic Play" description="Your decklist." noindex />
      <section className="play-card" data-testid="deck-view">
        <div className="play-deck__head">
          <div>
            <p className="play-eyebrow">Decklist</p>
            <h1 className="play-title">{existing?.name || 'Your decklist'}</h1>
            <p className="play-helper">
              {existing?.mainCount} main · {existing?.sideCount} sideboard
            </p>
          </div>
          {locked ? (
            <span className="play-status-chip">
              <FontAwesomeIcon icon={faLock} style={{ marginRight: 6 }} /> Locked
            </span>
          ) : (
            <button type="button" className="play-text-button" onClick={() => setEditing(true)} data-testid="deck-edit">
              Edit
            </button>
          )}
        </div>
        {locked && <p className="play-deck__locked">Locked when round 1 started. Ask a judge for changes.</p>}
        <DeckList title="Main" cards={sections.main} />
        {sections.side.length > 0 && <DeckList title="Sideboard" cards={sections.side} />}
      </section>
    </PlayLayout>
  );
};

const DeckList: React.FC<{ title: string; cards: { quantity?: number; name: string }[] }> = ({ title, cards }) => (
  <div className="play-deck__section">
    <p className="play-deck__section-title">{title}</p>
    <ul className="play-deck__list">
      {cards.map((c, i) => (
        <li key={`${c.name}-${i}`}>
          <span className="play-deck__qty">{c.quantity ?? 1}</span>
          <span className="play-deck__name">{c.name}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default DeckPage;
